import { MouseEvent as ReactMouseEvent } from 'react';

import { useSetOverlayState } from './useSetOverlayState.js';
import { useTooltip } from './useTooltip.js';
import { LABELS_DISPLAY } from '../constants.js';
import { useLabelsDisplay } from '../context/LabelsDisplayContext.js';
import {
  useClusterNodesState,
  useLeafNodesState,
  useToolbarHoverState,
  useTooltipPinnedState,
  useTooltipVisibleState,
} from '../store/selectors.js';
import { getTooltipData } from '../utils/get-tooltip-data.js';
import { getTooltipPosition } from '../utils/get-tooltip-position.js';

export const useOverlayHandlers = () => {
  const hierarchyLeafNodes = useLeafNodesState();
  const hierarchyClusterNodes = useClusterNodesState();
  const {
    setTooltipContent,
    setTooltipPinned,
    setTooltipPosition,
    setTooltipVisible,
  } = useTooltip();

  const pinned = useTooltipPinnedState();
  const visible = useTooltipVisibleState();
  const labelsDisplay = useLabelsDisplay();
  const isToolbarHovered = useToolbarHoverState();
  const { setOverlayStateInBounds } = useSetOverlayState();

  const parentLabels: string[] = [LABELS_DISPLAY.All, LABELS_DISPLAY.Clusters];
  const hasParentLabels = parentLabels.includes(labelsDisplay);

  const onMouseMoveHandler = (event: ReactMouseEvent<HTMLDivElement>) => {
    const { position, relativePosition } = getTooltipPosition(event);
    const tooltipData = getTooltipData(
      hierarchyLeafNodes,
      hierarchyClusterNodes,
      relativePosition,
      hasParentLabels,
    );

    if (tooltipData && !pinned && !isToolbarHovered) {
      setTooltipVisible(true);
      setTooltipContent(tooltipData);
      setTooltipPosition(position);
    } else if (!pinned) {
      setTooltipVisible(false);
    }
  };

  const onMouseEnterHandler = () => {
    setOverlayStateInBounds(true);
  };

  const onMouseLeaveHandler = () => {
    setOverlayStateInBounds(false);
  };

  const onClickHandler = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const { position, relativePosition } = getTooltipPosition(event);
    const tooltipData = getTooltipData(
      hierarchyLeafNodes,
      hierarchyClusterNodes,
      relativePosition,
    );
    if (tooltipData) {
      setTooltipContent(tooltipData);
      setTooltipPosition(position);
    } else {
      setTooltipVisible(false);
    }

    visible && setTooltipPinned(!pinned);
  };

  return {
    onClickHandler,
    onMouseMoveHandler,
    onMouseLeaveHandler,
    onMouseEnterHandler,
  };
};
